// src/screens/ChatScreen.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import "../styles/LegalPage.css";

const API_BASE = process.env.REACT_APP_API_URL;

const token =
  typeof window !== "undefined" ? localStorage.getItem("token") : null;
const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

export default function ChatScreen() {
  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [error, setError] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!API_BASE || !token) {
      setLoadingChats(false);
      setError("You must be logged in to view messages.");
      return;
    }

    let cancelled = false;

    const fetchChats = async () => {
      try {
        setLoadingChats(true);
        const res = await axios.get(`${API_BASE}/chat`, {
          headers: authHeader,
        });
        if (cancelled) return;
        setChats(res.data.chats || []);
      } catch (err) {
        console.error("GET /chat error:", err);
        if (!cancelled) {
          setError("Failed to load conversations.");
        }
      } finally {
        if (!cancelled) {
          setLoadingChats(false);
        }
      }
    };

    fetchChats();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatTime = (ts) => {
    if (!ts) return "";
    try {
      const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const openChat = async (chat) => {
    if (!API_BASE || !token) return;
    setSelectedChat(chat);
    setLoadingMessages(true);
    setMessages([]);
    setError("");

    try {
      const res = await axios.get(`${API_BASE}/chat/${chat.id}/messages`, {
        headers: authHeader,
      });
      const { chat: updatedChat, messages: msgs } = res.data;
      setSelectedChat(updatedChat || chat);
      setMessages(msgs || []);

      // Mark messages as read (best-effort)
      if ((msgs || []).length) {
        axios
          .post(
            `${API_BASE}/chat/${chat.id}/read`,
            {},
            { headers: authHeader }
          )
          .catch(() => {});
      }
    } catch (err) {
      console.error("GET /chat/:chatId/messages error:", err);
      setError("Failed to load conversation.");
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedChat || !messageText.trim() || !API_BASE || !token) return;

    const text = messageText.trim();
    setSending(true);

    try {
      const res = await axios.post(
        `${API_BASE}/chat/${selectedChat.id}/messages`,
        { text },
        { headers: authHeader }
      );
      const newMsg = res.data.message;
      setMessages((prev) => [...prev, newMsg]);
      setMessageText("");

      // Update chat preview in list
      setChats((prev) =>
        prev.map((c) =>
          c.id === selectedChat.id
            ? {
                ...c,
                lastMessageText: text,
                lastMessageAt: newMsg.createdAt || c.lastMessageAt,
              }
            : c
        )
      );
    } catch (err) {
      console.error("POST /chat/:chatId/messages error:", err);
      setError("Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const renderList = () => (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h1>Messages</h1>
      </div>

      <p className="legal-meta">
        {loadingChats
          ? "Loading your conversations..."
          : chats.length === 0
          ? "No messages yet. You can message hosts after you book a visit."
          : `${chats.length} conversation${
              chats.length > 1 ? "s" : ""
            }`}
      </p>

      {error && (
        <p style={{ color: "#b91c1c", fontSize: 13, marginBottom: 8 }}>
          {error}
        </p>
      )}

      {!loadingChats && chats.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {chats.map((chat) => (
            <li
              key={chat.id}
              onClick={() => openChat(chat)}
              style={{
                padding: "10px 0",
                borderBottom: "1px solid #e5e7eb",
                cursor: "pointer",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {chat.locationName || "Bathroom visit"}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#4b5563",
                  marginTop: 2,
                }}
              >
                {chat.lastMessageText || "Tap to open conversation"}
              </div>
              {chat.lastMessageAt && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#9ca3af",
                    marginTop: 2,
                  }}
                >
                  {formatTime(chat.lastMessageAt)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );

  const renderThread = () => (
    <>
      <button
        type="button"
        onClick={() => {
          setSelectedChat(null);
          setMessages([]);
          setError("");
        }}
        style={{
          fontSize: 12,
          padding: "4px 10px",
          borderRadius: 999,
          border: "1px solid #e5e7eb",
          background: "#f9fafb",
          marginBottom: 8,
          cursor: "pointer",
        }}
      >
        ← Back to messages
      </button>

      <h1 style={{ marginBottom: 4 }}>
        {selectedChat?.locationName || "Bathroom visit"}
      </h1>
      <p className="legal-meta">
        Chat with your host about access details and any issues during your
        visit.
      </p>

      {error && (
        <p style={{ color: "#b91c1c", fontSize: 13, marginBottom: 8 }}>
          {error}
        </p>
      )}

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 8,
          maxHeight: "50vh",
          overflowY: "auto",
          marginBottom: 8,
          background: "#f9fafb",
        }}
      >
        {loadingMessages ? (
          <p style={{ fontSize: 13, color: "#6b7280" }}>
            Loading conversation...
          </p>
        ) : messages.length === 0 ? (
          <p style={{ fontSize: 13, color: "#6b7280" }}>
            No messages yet. Start the conversation below.
          </p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: 6,
              }}
            >
              <div
                style={{
                  maxWidth: "80%",
                  padding: "6px 10px",
                  borderRadius: 10,
                  background: "#ffffff",
                  fontSize: 13,
                  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
                }}
              >
                <div style={{ marginBottom: 2 }}>{m.text}</div>
                <div
                  style={{
                    fontSize: 10,
                    color: "#9ca3af",
                    textAlign: "right",
                  }}
                >
                  {formatTime(m.createdAt)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <form onSubmit={handleSend} style={{ display: "flex", gap: 8 }}>
        <input
          type="text"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder="Type a message..."
          style={{
            flex: 1,
            borderRadius: 999,
            border: "1px solid #e5e7eb",
            padding: "8px 12px",
            fontSize: 13,
          }}
        />
        <button
          type="submit"
          disabled={sending || !messageText.trim()}
          style={{
            borderRadius: 999,
            border: "none",
            padding: "8px 14px",
            fontSize: 13,
            background: "#111827",
            color: "#ffffff",
            cursor: "pointer",
            opacity: sending || !messageText.trim() ? 0.6 : 1,
          }}
        >
          Send
        </button>
      </form>
    </>
  );

  return (
    <div className="legal-page">
      <div className="legal-card">
        {selectedChat ? renderThread() : renderList()}
      </div>
    </div>
  );
}

